import { LoadingButton } from '@atlaskit/button';
import { Divider, DynamicResultTable, FormattedAddress, useFeatureToggle } from '@css-xp/base';
import {
  InvoiceGroup,
  FetchBillingProfilesForTransactionAccountQuery,
  FetchBillingProfilesForTransactionAccountQueryVariables,
  PaymentMethod,
  CreditCard,
  PaymentMethodType,
  PaymentOnTerms,
  Maybe,
  BillingFrequency,
} from '@css-xp/graphtypes/src/generated-types-new';
import InfoIcon from '@atlaskit/icon/glyph/info';

import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { FormData } from '../subscription-txa-transfer-pane';
import { fetchBillingProfilesForTransactionAccount } from '../../../../../../billing-v2/tabs/invoice-group-tabs';
import { capitalize } from 'lodash';
import Lozenge from '@atlaskit/lozenge';
import { ENABLE_CREDIT_MANAGEMENT } from '@atlassian/cssxp-utils-generic';
import './progress-form.scss';
import styled from '@emotion/styled';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';

const InlineDialog = styled(TooltipPrimitive)`
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  box-sizing: content-box; /* do not set this to border-box or it will break the overflow handling */
  color: #333;
  max-height: 300px;
  max-width: 300px;
  padding: 8px 12px;
`;

type Props = {
  formState: FormData;
  selectBillingProfile: (billingProfile: InvoiceGroup) => void;
  billingFrequency?: Maybe<string> | undefined;
  endTimestamp?: Maybe<number> | undefined;
};

const defaultSearchOptions = {
  first: DEFAULT_ROWS_PER_PAGE,
  offset: 0,
};

export const emptyStateBillingProfileProp = {
  header: '',
  description: 'No billing-profiles found',
  imageUrl: '',
  testId: 'no-results-billingProfile-state',
};

export const getInvoiceGroupName = (name?: string | null | undefined) => {
  if (!name || name?.toLowerCase() === 'none') {
    return `Unnamed Billing Profile`;
  }
  return name;
};

export const formatPaymentMethod = (paymentMethod?: PaymentMethod) => {
  if (paymentMethod?.type === PaymentMethodType.CARD) {
    const creditCard = paymentMethod as CreditCard;
    return (
      <div>
        &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; {creditCard?.card?.last4Numbers}
        <div>
          Expires {('0' + creditCard?.card?.expirationMonth).slice(-2)}/{creditCard?.card?.expirationYear?.slice(-2)}
        </div>
      </div>
    );
  }
  if (paymentMethod?.type === PaymentMethodType.DEFERRED) {
    const paymentOnTerms = paymentMethod as PaymentOnTerms;
    return <span>Payment on terms: due in {paymentOnTerms.terms?.invoiceDueDays} days</span>;
  }
};

export const BillingProfileTable = ({ formState, selectBillingProfile, billingFrequency, endTimestamp }: Props) => {
  const [isEnabled] = useFeatureToggle();
  const isCreditManagementEnabled = isEnabled(ENABLE_CREDIT_MANAGEMENT);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const transactionAccountId = formState?.selectedBillingAccount?.id;
  const { refetch, data, loading } = useQuery<
    FetchBillingProfilesForTransactionAccountQuery,
    FetchBillingProfilesForTransactionAccountQueryVariables
  >(fetchBillingProfilesForTransactionAccount, {
    variables: { id: transactionAccountId ?? '', searchOptions: defaultSearchOptions },
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'cache-and-network',
    skip: !transactionAccountId,
  });

  const isAnnualOrMonthlyEvergreen = !(billingFrequency === BillingFrequency.ANNUAL && Boolean(endTimestamp));

  const onPageChange = (newPage: number) => {
    if (currentPage === newPage) {
      return;
    }
    setCurrentPage(newPage);
    refetch({
      id: transactionAccountId,
      searchOptions: {
        ...defaultSearchOptions,
        offset: newPage !== 1 ? newPage * rowsPerPage - rowsPerPage : 0,
        first: rowsPerPage,
      },
    });
  };

  const onRowPerPageChange = (newRowPerPage: number) => {
    if (newRowPerPage === rowsPerPage) {
      return;
    }
    setCurrentPage(1);
    setRowsPerPage(newRowPerPage);
    refetch({
      id: transactionAccountId,
      searchOptions: {
        ...defaultSearchOptions,
        offset: 0,
        first: newRowPerPage,
      },
    });
  };

  // variables to use for paging and populating our template
  const totalCount = data?.transactionAccount?.billingProfiles?.totalCount ?? 0;
  const results = data?.transactionAccount?.billingProfiles?.nodes ?? [];

  const showPagination = totalCount > DEFAULT_ROWS_PER_PAGE;

  const wrapInCell = (node: React.ReactElement | string) => {
    return <div className="pv-8">{node}</div>;
  };

  const getRows = () => {
    return (results ?? []).map((billingProfile, index) => {
      const hasTermPayments = billingProfile?.defaultPaymentMethod?.type === 'DEFERRED';
      return {
        key: billingProfile?.id ?? index.toString(),
        cells: [
          {
            content: wrapInCell(
              billingProfile?.id ? (
                <div>
                  {getInvoiceGroupName(billingProfile?.name)}
                  {billingProfile?.defaultPaymentMethod?.paymentIssue && (
                    <Lozenge appearance="removed">PAYMENT ERROR</Lozenge>
                  )}
                </div>
              ) : (
                'Not available'
              )
            ),
          },
          {
            content: wrapInCell(
              billingProfile?.shipToParty?.id ? (
                <div>
                  <FormattedAddress
                    addressLine1={billingProfile?.shipToParty?.addressLine1}
                    addressLine2={billingProfile?.shipToParty?.addressLine2}
                    city={billingProfile?.shipToParty?.city}
                    country={billingProfile?.shipToParty?.country}
                    postcode={billingProfile?.shipToParty?.postcode}
                    state={billingProfile?.shipToParty?.state}
                    organizationName={billingProfile?.shipToParty?.name}
                  />
                </div>
              ) : (
                'Not available'
              )
            ),
          },
          {
            content: wrapInCell(
              billingProfile?.shipToParty?.taxId ? billingProfile?.shipToParty?.taxId : 'Not provided'
            ),
          },
          {
            content: billingProfile?.defaultPaymentMethod
              ? formatPaymentMethod(billingProfile?.defaultPaymentMethod)
              : 'Not available',
          },
          {
            content: wrapInCell(
              billingProfile?.shipToPriceEligibility ? (
                capitalize(billingProfile?.shipToPriceEligibility)
              ) : (
                <div aria-label="Not applicable">-</div>
              )
            ),
          },
          {
            content: wrapInCell(billingProfile?.currency ?? 'N/A'),
          },
          {
            key: `${billingProfile?.id}-actions`,
            params: billingProfile,
            content:
              billingProfile?.id &&
              wrapInCell(
                <div className="select-acount-actions">
                  <LoadingButton
                    appearance="link"
                    spacing="compact"
                    onClick={() => selectBillingProfile(billingProfile as InvoiceGroup)}
                    isDisabled={isCreditManagementEnabled && isAnnualOrMonthlyEvergreen && hasTermPayments}
                    testId="select-billing-profile"
                  >
                    Select
                  </LoadingButton>
                </div>
              ),
          },
        ],
      };
    });
  };

  const getHead = () => {
    const cells = [
      {
        content: 'Name',
        width: 15,
      },
      {
        content: 'Sold-to address',
        width: 20,
      },
      {
        content: 'Tax ID',
        width: 15,
      },
      {
        content: 'Payment method',
        width: 20,
      },
      {
        content: 'Pricing',
        width: 10,
      },
      {
        content: 'Currency',
        width: 10,
      },
      {
        content: '',
      },
    ];

    return {
      cells,
    };
  };

  return (
    <div className="contact-billing-account-container-v2">
      <h2>Select billing profile</h2>
      <Divider />
      <div className="select-billing-profile-table-container">
        <div>
          <Tooltip
            component={InlineDialog}
            content="Not all billing profiles are supported for this subscription. Change the plan details to use an existing billing profile or create a new one with a different payment method."
          >
            <InfoIcon size="small" label="" primaryColor="#6554C0" />
          </Tooltip>
          Can't find the billing profile you're looking for?
        </div>
        <DynamicResultTable
          testId="billing-profiles"
          head={getHead()}
          rows={getRows()}
          emptyStateProps={emptyStateBillingProfileProp}
          isLoading={loading}
          totalCount={totalCount}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          isPaginationEnabled={showPagination}
          onRowChange={onRowPerPageChange}
        ></DynamicResultTable>
      </div>
    </div>
  );
};
